import { MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLicenseOrderAction } from './hooks/useLicenseOrders';
import type { LicenseOrderListItem } from './types';

interface Props {
  order: LicenseOrderListItem;
  invoiceBusy: boolean;
  onOpen: () => void;
  onChangeProduct: () => void;
  onDownloadInvoice: () => void;
  onNotice: (message: string, tone: 'ok' | 'error') => void;
}

export function canChangeLicenseProduct(order: LicenseOrderListItem): boolean {
  return Boolean(order.paidAt) && order.status !== 'refunded' && order.status !== 'cancelled';
}

export function LicenseOrderRowMenu({
  order,
  invoiceBusy,
  onOpen,
  onChangeProduct,
  onDownloadInvoice,
  onNotice,
}: Props) {
  const action = useLicenseOrderAction(order.id);
  const busy = action.isPending || invoiceBusy;
  const unpaid = order.status === 'received' || order.status === 'abandoned';

  async function run(body: Record<string, unknown>, ok: string) {
    try {
      await action.mutateAsync(body);
      onNotice(ok, 'ok');
    } catch (error) {
      onNotice(error instanceof Error ? error.message : 'No se ha podido completar la acción', 'error');
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Acciones de ${order.publicId}`}
          onClick={(event) => event.stopPropagation()}
          className="inline-flex items-center justify-center p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <MoreHorizontal size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onSelect={() => onOpen()}>Abrir ficha</DropdownMenuItem>
        {canChangeLicenseProduct(order) && (
          <DropdownMenuItem onSelect={() => onChangeProduct()}>Cambiar producto</DropdownMenuItem>
        )}
        {order.paidAt && (
          <DropdownMenuItem disabled={busy} onSelect={() => onDownloadInvoice()}>
            Descargar factura
          </DropdownMenuItem>
        )}
        {unpaid && (
          <DropdownMenuItem
            disabled={busy}
            onSelect={() => {
              void run(
                { action: 'send_payment_link' },
                'Correo enviado con el enlace de pago. Caduca en 24 horas.'
              );
            }}
          >
            Enviar enlace de pago
          </DropdownMenuItem>
        )}
        {order.status === 'received' && (
          <DropdownMenuItem
            disabled={busy}
            onSelect={() => {
              window.setTimeout(() => {
                const sendEmail = window.confirm(
                  '¿Marcar este pedido como abandonado y enviar el último correo con el enlace de pago?'
                );
                if (!sendEmail) return;
                void run(
                  { action: 'abandon', sendEmail: true },
                  'Marcado como abandonado y correo enviado.'
                );
              }, 0);
            }}
          >
            Marcar abandonado
          </DropdownMenuItem>
        )}
        {order.status === 'abandoned' && (
          <DropdownMenuItem
            disabled={busy}
            onSelect={() => {
              window.setTimeout(() => {
                if (!window.confirm('¿Devolver este pedido a Sin pagar?')) return;
                void run({ action: 'restore_unpaid' }, 'El pedido vuelve a Sin pagar.');
              }, 0);
            }}
          >
            Volver a Sin pagar
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={() => {
            void navigator.clipboard.writeText(order.contactEmail).then(
              () => onNotice('Email copiado.', 'ok'),
              () => onNotice('No se ha podido copiar el email.', 'error')
            );
          }}
        >
          Copiar email
        </DropdownMenuItem>
        {order.contactPhone ? (
          <DropdownMenuItem
            onSelect={() => {
              void navigator.clipboard.writeText(order.contactPhone || '').then(
                () => onNotice('Teléfono copiado.', 'ok'),
                () => onNotice('No se ha podido copiar el teléfono.', 'error')
              );
            }}
          >
            Copiar teléfono
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
